/* Spark review list — Yotpo's reviews, rendered by this theme.
 *
 * ONE RENDERER, TWO SCOPES. The PDP section (snippets/spark-pdp-reviews.liquid)
 * shows one product's reviews; the reviews page (snippets/spark-store-reviews
 * .liquid) shows the whole store's. They were never meant to look like two
 * different things — the store page ran Yotpo's own "all reviews" widget, with
 * its own type, its own stars, its own pagination and five rows of chrome above
 * the first review, beside a PDP that renders the same reviews as this theme's
 * own cards. So the renderer lives here and both sections mount it.
 *
 * Everything about a mount is read off its root element:
 *   data-spark-rev            the mount point (required)
 *   data-yotpo-key            Yotpo app key (required)
 *   data-product-id           scope to one product; omit for the whole store
 *   data-show-product         print which product each review is about
 *   data-per                  page size, default 6
 *
 * The endpoints are the public ones Yotpo's own widgets call — same reviews,
 * same moderation, no bundle:
 *   product : api-cdn.yotpo.com/v1/widget/<key>/products/<id>/reviews.json
 *   store   : api-cdn.yotpo.com/v1/widget/<key>/reviews.json
 *
 * Utility class strings below are literal on purpose — Tailwind scans this file
 * (see the @source line in spark-theme.css) and only finds classes it can read.
 */
(function () {
  var STARS = '★★★★★';

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    /* textContent, never innerHTML — every value below is customer-authored. */
    if (text != null) n.textContent = text;
    return n;
  }

  /* Same two-layer markup as snippets/spark-stars.liquid at text-body-lg, so a
     JS-rendered row and a Liquid-rendered one are the same object: a muted base
     and a gold overlay clipped to score/5, which gives a true fractional fifth
     star. */
  function stars(score) {
    var pct = Math.max(0, Math.min(100, (Number(score) || 0) * 20));
    var wrap = el('div', 'relative inline-block text-body-lg leading-none whitespace-nowrap');
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', (Math.round(Number(score) * 10) / 10) + ' out of 5 stars');
    var base = el('span', 'block text-border-2', STARS);
    base.setAttribute('aria-hidden', 'true');
    var fill = el('span', 'absolute inset-y-0 left-0 block overflow-hidden text-star', STARS);
    fill.setAttribute('aria-hidden', 'true');
    fill.style.width = pct + '%';
    wrap.appendChild(base);
    wrap.appendChild(fill);
    return wrap;
  }

  /* The initial on a muted disc. Same 56px circle and border as the curated
     fallback cards, so the left column lines up either way. */
  function avatar(name) {
    var a = el(
      'span',
      'flex h-14 w-14 flex-none items-center justify-center rounded-full border border-card-border bg-muted font-serif text-body-lg text-foreground-soft',
      (name || '?').trim().charAt(0).toUpperCase()
    );
    a.setAttribute('aria-hidden', 'true');
    return a;
  }

  /* snippets/spark-verified-badge.liquid, rebuilt. innerHTML is a fixed string with
     no interpolation — the name and the review body never touch it. */
  function verified() {
    var s = el('span', 'inline-flex items-center gap-1.5 text-caption font-medium text-primary-text');
    s.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
    s.appendChild(document.createTextNode('Verified buyer'));
    return s;
  }

  /* Yotpo's review TITLES are mostly auto-generated from the first words of the
     body, so printing both shows the same sentence twice. Keep one only when it
     is a separate thought. */
  function shouldTitle(title, body) {
    var t = (title || '').trim();
    var c = (body || '').trim();
    if (t.length < 3) return '';
    var tl = t.toLowerCase();
    var cl = c.toLowerCase();
    if (tl === cl) return '';
    if (cl.indexOf(tl) === 0) return '';
    return t;
  }

  /* The dedupe key, computed the same way in snippets/spark-pdp-reviews.liquid:
     the first 60 characters of the trimmed, lower-cased body. The body is what
     the server and Yotpo agree on exactly; display names vary in case and
     punctuation between the two, so they are not part of it. */
  function sig(body) {
    return (body || '').trim().toLowerCase().slice(0, 60);
  }

  function fmtDate(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function mount(root) {
    var KEY = root.getAttribute('data-yotpo-key');
    if (!KEY) return;
    var PID = root.getAttribute('data-product-id') || '';
    var showProduct = root.hasAttribute('data-show-product');

    var listEl = root.querySelector('[data-spark-rev-list]');
    var fallbackEl = root.querySelector('[data-spark-rev-fallback]');

    /* SERVER-RENDERED REVIEWS. The PDP prints its newest reviews into the list on
       the server (snippets/spark-review-card.liquid) so they are in the HTML for
       clients that never run this file. They are real reviews, not placeholders,
       so this script adds to them rather than replacing them: it keeps them on
       screen, skips any fetched review already showing, and — when Yotpo cannot be
       reached — leaves them exactly where they are.

       /pages/reviews seeds nothing, so seeds.length is 0 there and every path
       below behaves as it did before. */
    var seeds = listEl.querySelectorAll('[data-spark-rev-seed]');
    var seen = {};
    for (var s = 0; s < seeds.length; s++) seen[seeds[s].getAttribute('data-rev-sig')] = 1;
    var moreBtn = root.querySelector('[data-spark-rev-more]');
    var statusEl = root.querySelector('[data-spark-rev-status]');
    var avgEl = root.querySelector('[data-spark-rev-avg]');
    var countEl = root.querySelector('[data-spark-rev-count]');
    if (!listEl || !moreBtn) return;

    /* Six, then "Load more". One card per row makes a larger first page a long
       scroll before the shopper reaches anything else on the page. */
    var PER = parseInt(root.getAttribute('data-per'), 10) || 6;
    var page = 1;
    var total = 0;
    /* Seeded cards are already on screen, so they count toward "Showing N of M"
       and toward whether there is anything left to load. */
    var shown = seeds.length;
    var loading = false;
    var started = false;
    /* product id -> name, rebuilt from every response: the store-wide feed says
       which product a review is about only by id. */
    var names = {};

    /* One review card. Keep the class strings literal so Tailwind's scanner
       finds them. */
    function review(rv) {
      var art = el('article', 'spark-rev flex flex-col gap-3 rounded-card border border-card-border bg-white p-6 sm:flex-row sm:items-start sm:gap-8');
      var name = (rv.user && rv.user.display_name) || 'Anonymous';

      /* WHO — above the review below 640px, beside it above. The fixed 190px column
         is what lets the names line up down the page instead of each card starting
         its quote at a different x. */
      var person = el('div', 'sm:w-[190px] sm:flex-none');
      var row = el('div', 'flex items-center gap-3');
      row.appendChild(avatar(name));
      var who = el('div', 'min-w-0');
      who.appendChild(el('p', 'text-body-sm font-semibold text-foreground-soft', name));
      /* Not every review is verified — so the badge is per review and never a
         blanket claim in the heading. */
      if (rv.verified_buyer) who.appendChild(verified());
      row.appendChild(who);
      person.appendChild(row);
      /* The date belongs with the person, not pinned to the bottom of the card: in a
         row layout there is no tall card left for it to close. */
      var when = fmtDate(rv.created_at);
      if (when) person.appendChild(el('p', 'mt-2.5 text-body-sm text-foreground-soft', when));
      art.appendChild(person);

      /* WHAT THEY SAID */
      var main = el('div', 'flex min-w-0 flex-1 flex-col gap-3');
      main.appendChild(stars(rv.score));

      var title = shouldTitle(rv.title, rv.content);
      if (title) main.appendChild(el('h3', 'font-serif text-body-lg font-normal text-foreground', title));

      main.appendChild(el(
        'p',
        'text-body text-foreground-soft [display:-webkit-box] [-webkit-line-clamp:6] [-webkit-box-orient:vertical] overflow-hidden',
        '“' + (rv.content || '').trim() + '”'
      ));

      /* WHICH PRODUCT — store scope only. On a PDP every review is about the
         product the shopper is looking at, so the line would be the same
         sentence under every card. */
      if (showProduct && names[rv.product_id]) {
        main.appendChild(el(
          'p',
          'mt-1 text-caption text-muted-foreground',
          names[rv.product_id]
        ));
      }

      art.appendChild(main);
      return art;
    }

    function say(msg) { if (statusEl) statusEl.textContent = msg; }

    function fail() {
      /* Real reviews already on the page beat anything this can put in their
         place, so a product that server-rendered its reviews simply keeps them. */
      if (seeds.length) {
        moreBtn.hidden = true;
        say('');
        return;
      }
      listEl.innerHTML = '';
      if (fallbackEl) fallbackEl.hidden = false;
      moreBtn.hidden = true;
      say('');
    }

    function endpoint() {
      var base = 'https://api-cdn.yotpo.com/v1/widget/' + KEY;
      var path = PID ? '/products/' + PID + '/reviews.json' : '/reviews.json';
      return base + path + '?per_page=' + PER + '&page=' + page + '&sort=date';
    }

    function load(append) {
      if (loading) return;
      loading = true;
      if (append) moreBtn.textContent = 'Loading…';

      fetch(endpoint())
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
        .then(function (j) {
          var resp = (j && j.response) || {};
          var reviews = resp.reviews || [];

          (resp.products || []).forEach(function (p) {
            if (p && p.id && p.name) names[p.id] = p.name;
          });

          if (page === 1) {
            var b = resp.bottomline || {};
            total = b.total_review || (resp.pagination && resp.pagination.total) || 0;
            /* Refine the server-rendered summary rather than replace it — if Yotpo
               and the metafield disagree, Yotpo is the fresher number. */
            if (avgEl && b.average_score) avgEl.textContent = (Math.round(b.average_score * 10) / 10).toFixed(1);
            if (countEl && total) countEl.textContent = total.toLocaleString() + ' reviews';
            /* Clear the "Loading reviews…" note and anything else this script put
               here — but never the server-rendered cards. */
            Array.prototype.slice.call(listEl.children).forEach(function (n) {
              if (!n.hasAttribute('data-spark-rev-seed')) listEl.removeChild(n);
            });
            if (!reviews.length && !seeds.length) {
              listEl.appendChild(el('p', 'spark-rev__note', 'No reviews yet.'));
              moreBtn.hidden = true;
              loading = false;
              return;
            }
          }

          var frag = document.createDocumentFragment();
          var added = 0;
          reviews.forEach(function (rv) {
            var k = sig(rv.content);
            if (seen[k]) return;
            seen[k] = 1;
            frag.appendChild(review(rv));
            added++;
          });
          listEl.appendChild(frag);

          shown += added;
          page++;
          moreBtn.textContent = 'Load more reviews';
          moreBtn.hidden = shown >= total || reviews.length < PER;
          if (append) say('Showing ' + shown.toLocaleString() + ' of ' + total.toLocaleString() + ' reviews');
          loading = false;
        })
        .catch(function () {
          loading = false;
          moreBtn.textContent = 'Load more reviews';
          /* Only the FIRST page failing is fatal — a failed "load more" should leave
             what is already on screen alone. */
          if (!append) fail();
        });
    }

    moreBtn.addEventListener('click', function () { load(true); });

    function start() {
      if (started) return;
      started = true;
      load(false);
    }

    /* Below the fold — start the fetch as the section approaches. */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) { io.disconnect(); start(); }
      }, { rootMargin: '600px' });
      io.observe(root);
      /* BACKSTOP. An observer whose root viewport is zero-sized never reports an
         intersection and never will — the callback simply does not run, so the
         section sits on "Loading reviews…" permanently. Found in an embedded
         browser pane collapsed to 0px, but the same holds for any container that
         starts at zero size, and a stuck spinner is a worse outcome than fetching
         a few seconds early on a page nobody scrolled.

         Deliberately not tied to a scroll or resize listener: the failure is the
         absence of events, so the fix cannot depend on one arriving. */
      setTimeout(function () {
        if (started) return;
        io.disconnect();
        start();
      }, 4000);
    } else {
      start();
    }
  }

  var roots = document.querySelectorAll('[data-spark-rev]');
  for (var i = 0; i < roots.length; i++) mount(roots[i]);
})();
